import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'
import type { ESignImage } from '../../types/e-sign.types'

/**
 * A doctor as the booking flow and the agenda detail show them: the photo beside the name, with a
 * line for the specialisation and a note or two under it. The avatar and the Details column sit
 * side by side, centred on each other however many lines the call site composes into Details.
 * What the lines say is up to the call site.
 */

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo
 * ------------------------------------------------------------------------------------------------- */

type DoctorInfoRootProps = ComponentPropsWithoutRef<'div'>

const DoctorInfoRoot = ({ children, ...props }: DoctorInfoRootProps) => <StRoot {...props}>{children}</StRoot>

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo.Avatar
 * ------------------------------------------------------------------------------------------------- */

type DoctorInfoAvatarSize = 'small' | 'large'

type DoctorInfoAvatarProps = ComponentPropsWithoutRef<'div'> & {
  images?: Partial<ESignImage>
  /** Names the photo for assistive technology; the placeholder stays unnamed. */
  name: string
  size?: DoctorInfoAvatarSize
}

const PLACEHOLDER_SRC = '/img/svg/doctor-male.svg'

// eSign only reliably delivers the 479px sizes and the original; fall back through the available
// sizes so a photo is shown whenever one exists. The large avatar prefers 2x so it stays sharp.
const pickImageSrc = (images: Partial<ESignImage> | undefined, size: DoctorInfoAvatarSize) => {
  const dpis = size === 'large' ? (['2x', '1x'] as const) : (['1x', '2x'] as const)

  for (const dpi of dpis) {
    const src = images?.['480px']?.[dpi] || images?.['479px']?.[dpi] || images?.['768px']?.[dpi]
    if (src) {
      return src
    }
  }

  return images?.original || ''
}

const DoctorInfoAvatar = ({ images, name, size = 'small', ...props }: DoctorInfoAvatarProps) => {
  const src = pickImageSrc(images, size)

  return (
    <StAvatar $size={size} {...props}>
      {src ? (
        <StPhoto
          src={src}
          alt={name}
          onError={({ currentTarget }) => {
            currentTarget.src = PLACEHOLDER_SRC
          }}
        />
      ) : (
        <img src={PLACEHOLDER_SRC} alt='' />
      )}
    </StAvatar>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo.Details
 * ------------------------------------------------------------------------------------------------- */

// The column of text beside the avatar: Name, Description and Notes stack in it in the order composed.
type DoctorInfoDetailsProps = ComponentPropsWithoutRef<'div'>

const DoctorInfoDetails = ({ children, ...props }: DoctorInfoDetailsProps) => (
  <StDetails {...props}>{children}</StDetails>
)

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo.Name
 * ------------------------------------------------------------------------------------------------- */

// The call site picks the type: the large avatar goes with a bold, larger name.
type DoctorInfoNameProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'> & {
  type?: ComponentPropsWithoutRef<typeof Span>['type']
}

const DoctorInfoName = ({ children, type = 'BodyMediumRegular', ...props }: DoctorInfoNameProps) => (
  <StText type={type} {...props}>
    {children}
  </StText>
)

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo.Description
 * ------------------------------------------------------------------------------------------------- */

// The line under the name: the specialisation.
type DoctorInfoDescriptionProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const DoctorInfoDescription = ({ children, ...props }: DoctorInfoDescriptionProps) => (
  <StSecondaryText type='BodyMediumRegular' {...props}>
    {children}
  </StSecondaryText>
)

/* ------------------------------------------------------------------------------------------------- *
 * DoctorInfo.Note
 * ------------------------------------------------------------------------------------------------- */

// A small line under the description: the fields of specialisation, the convention status.
type DoctorInfoNoteProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const DoctorInfoNote = ({ children, ...props }: DoctorInfoNoteProps) => (
  <StSecondaryText type='BodySmallRegular' {...props}>
    {children}
  </StSecondaryText>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const DoctorInfo = Object.assign(DoctorInfoRoot, {
  Avatar: DoctorInfoAvatar,
  Details: DoctorInfoDetails,
  Name: DoctorInfoName,
  Description: DoctorInfoDescription,
  Note: DoctorInfoNote,
})

export { DoctorInfo }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The design's avatar sizes. 40 is on the spacing scale, 60 is not, so both are named here.
const AVATAR_SIZE_PX: Record<DoctorInfoAvatarSize, number> = { small: 40, large: 60 }

// Flex rather than a grid spanning the text rows: a spanning avatar gives its spare height to the
// rows below the text, which pushes the text above its centre.
const StRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const StAvatar = styled.div<{ $size: DoctorInfoAvatarSize }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => AVATAR_SIZE_PX[$size]}px;
  height: ${({ $size }) => AVATAR_SIZE_PX[$size]}px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
`

const StPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StText = styled(Span)``

const StSecondaryText = styled(StText)`
  color: ${({ theme, color }) => color ?? theme.Colors.Neutrals.Darker};
`
